import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FileText, 
  Download,
  Eye,
  Calendar,
  TrendingUp,
  Building2
} from 'lucide-react';
import NavBar from '../components/NavBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchReports();
  }, []);
  
  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getBenchmarkLabel = (position) => {
    const labels = {
      digital_leader: { tr: 'Dijital Lider', en: 'Digital Leader', color: 'text-emerald-400' },
      above_average: { tr: 'Ortalamanın Üstünde', en: 'Above Average', color: 'text-blue-400' },
      industry_average: { tr: 'Sektör Ortalaması', en: 'Industry Average', color: 'text-yellow-400' },
      below_average: { tr: 'Ortalamanın Altında', en: 'Below Average', color: 'text-orange-400' },
      digitally_lagging: { tr: 'Dijital Geciken', en: 'Digitally Lagging', color: 'text-red-400' }
    };
    return labels[position] || { tr: position, en: position, color: 'text-slate-400' };
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavBar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-white">{t('loading')}</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950" data-testid="reports-page">
      <NavBar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">{t('reports')}</h1>
          <p className="text-slate-400">
            {language === 'tr' 
              ? 'AI destekli dijital olgunluk raporlarınız'
              : 'Your AI-powered digital maturity reports'}
          </p>
        </div>
        
        {/* Reports List */}
        {reports.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reports.map((report) => (
              <Card 
                key={report.id}
                className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors"
                data-testid={`report-card-${report.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-lg bg-blue-500/10 text-blue-400">
                      <FileText size={24} />
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full bg-slate-800 ${getBenchmarkLabel(report.benchmark_position).color}`}>
                      {getBenchmarkLabel(report.benchmark_position)[language]}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white mb-2">{report.company_name}</h3>
                  
                  <div className="space-y-2 text-sm text-slate-400 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center space-x-2">
                        <TrendingUp size={14} />
                        <span>GDMII Score</span>
                      </span>
                      <span className="text-white font-semibold">{report.gdmii_score?.toFixed(1)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center space-x-2">
                        <Calendar size={14} />
                        <span>{language === 'tr' ? 'Tarih' : 'Date'}</span>
                      </span>
                      <span>{formatDate(report.created_at)}</span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 pt-4 border-t border-slate-800">
                    <Link to={`/reports/${report.id}`} className="flex-1">
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        data-testid={`view-report-${report.id}`}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('view')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-16 text-center">
              <FileText className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                {language === 'tr' ? 'Henüz rapor yok' : 'No reports yet'}
              </h3>
              <p className="text-slate-400 mb-6">
                {language === 'tr' 
                  ? 'Değerlendirme tamamladıktan sonra rapor oluşturabilirsiniz'
                  : 'Complete an assessment to generate reports'}
              </p>
              <Link to="/assessments">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  {t('assessments')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ReportsPage;
